import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch } from "../../../core/data/redux/store";
import type { Interview } from "../../../core/data/redux/recruitment/types";
import {
  selectInterviews,
  selectInterviewsLoading,
  selectPendingFeedbacks,
} from "../../../core/data/redux/recruitment/selectors";
import {
  fetchInterviews,
  fetchPendingFeedbacks,
  cancelInterview,
  completeInterview,
  updateInterviewStatus,
} from "../../../core/data/redux/recruitment/thunks";

import InterviewFilters, {
  type InterviewFilters as Filters,
} from "./components/InterviewFilters";
import InterviewTable from "./components/InterviewTable";
import FeedbackForm from "./components/FeedbackForm";
import EditInterviewModal from "./components/EditInterviewModal";
import RoundTemplateBuilder from "./components/RoundTemplateBuilder";
import RecruitmentHeader from "../components/RecruitmentHeader";
import Footer from "../components/Footer";
import { notify } from "../../../services/toast.config";

const TAB_ITEMS = [
  { key: "interviews", label: "Interviews", icon: "ti-calendar-event" },
  { key: "templates", label: "Round Templates", icon: "ti-template" },
];

const InterviewManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const interviews = useSelector(selectInterviews);
  const loading = useSelector(selectInterviewsLoading);
  const pendingFeedbacks = useSelector(selectPendingFeedbacks);
  const activeCompany = useSelector(
    (state: any) => state.recruitment.activeCompany,
  );

  const [activeTab, setActiveTab] = useState("interviews");
  const [filters, setFilters] = useState<Filters>({
    search: "",
    status: "",
    date_from: undefined,
    date_to: undefined,
  });

  const [feedbackModal, setFeedbackModal] = useState<{
    show: boolean;
    interviewId: number;
    candidateName: string;
  }>({ show: false, interviewId: 0, candidateName: "" });

  const [editModal, setEditModal] = useState<{
    show: boolean;
    interview: Interview | null;
  }>({ show: false, interview: null });

  useEffect(() => {
    if (!activeCompany?.company_id) return;
    dispatch(
      fetchInterviews({
        date_from: filters.date_from,
        date_to: filters.date_to,
      }),
    );
    dispatch(fetchPendingFeedbacks());
  }, [dispatch, activeCompany?.company_id, filters.date_from, filters.date_to]);

  const handleFilterChange = useCallback(
    (partial: Partial<Filters>) => {
      setFilters((prev) => ({ ...prev, ...partial }));

      if (
        "date_from" in partial ||
        "date_to" in partial ||
        "status" in partial
      ) {
        const next = { ...filters, ...partial };
        dispatch(
          fetchInterviews({
            status: next.status || undefined,
            date_from: next.date_from,
            date_to: next.date_to,
          }),
        );
      }
    },
    [dispatch, filters],
  );

  const filtered = useMemo(() => {
    let list = interviews as Interview[];
    const q = filters.search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          (i.candidate?.full_name || i.candidate_name || "").toLowerCase().includes(q) ||
          (i.job?.job_title || i.job_title || "").toLowerCase().includes(q) ||
          (i.round_name || "").toLowerCase().includes(q),
      );
    }
    if (filters.status) {
      list = list.filter((i) => i.status === filters.status);
    }
    return list;
  }, [interviews, filters.search, filters.status]);

  const handleViewFeedback = useCallback(
    (interviewId: number) => {
      const interview = interviews.find((i: Interview) => i.interview_id === interviewId);
      setFeedbackModal({
        show: true,
        interviewId,
        candidateName: interview?.candidate?.full_name || interview?.candidate_name || "Candidate",
      });
    },
    [interviews],
  );

  const handleReschedule = useCallback(async (interview: Interview) => {
    setEditModal({ show: true, interview });
  }, []);

  const handleCancel = useCallback(
    async (interviewId: number) => {
      try {
        await dispatch(
          cancelInterview({
            interviewId,
            cancellationReason: "other",
            cancellationNotes: "Cancelled by HR",
          }),
        ).unwrap();
        notify.success("Interview cancelled");
        dispatch(fetchInterviews({}));
      } catch (err: any) {
        notify.error(
          typeof err === "string" ? err : "Failed to cancel interview",
        );
      }
    },
    [dispatch],
  );

  const handleComplete = useCallback(
    async (interviewId: number) => {
      try {
        await dispatch(completeInterview({ interviewId })).unwrap();
        notify.success("Interview marked as completed");
        dispatch(fetchInterviews({}));
      } catch (err: any) {
        notify.error(
          typeof err === "string" ? err : "Failed to complete interview",
        );
      }
    },
    [dispatch],
  );

  const handleStatusChange = useCallback(
    async (interviewId: number, newStatus: string) => {
      try {
        await dispatch(
          updateInterviewStatus({ interviewId, newStatus }),
        ).unwrap();
        notify.success(
          `Interview status updated to ${newStatus.replace(/_/g, " ")}`,
        );
        dispatch(fetchInterviews({}));
      } catch (err: any) {
        notify.error(
          typeof err === "string" ? err : "Failed to update interview status",
        );
      }
    },
    [dispatch],
  );

  const handleFeedbackSubmitted = useCallback(() => {
    dispatch(fetchInterviews({}));
    dispatch(fetchPendingFeedbacks());
  }, [dispatch]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <RecruitmentHeader title="Interviews" breadcrumb="Interviews">
          {pendingFeedbacks.length > 0 && (
            <span
              className="badge badge-lg bg-warning text-dark d-flex align-items-center gap-1"
              style={{ fontSize: 13 }}>
              <i className="ti ti-message-circle" />
              {pendingFeedbacks.length} pending feedback
            </span>
          )}
        </RecruitmentHeader>

        {/* Tabs */}
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            marginBottom: 16,
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "0 16px",
              borderBottom: "2px solid #f0f0f0",
            }}>
            {TAB_ITEMS.map(({ key, icon, label }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "14px 16px",
                    background: "transparent",
                    border: "none",
                    borderBottom: isActive
                      ? "2px solid #1677ff"
                      : "2px solid transparent",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 14,
                    color: isActive ? "#1677ff" : "#595959",
                    transition: "all 0.2s",
                    marginBottom: -1,
                  }}>
                  <i
                    className={`ti ${icon}`}
                    style={{
                      fontSize: 15,
                      color: isActive ? "#1677ff" : "#8c8c8c",
                    }}
                  />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === "interviews" && (
          <>
            <InterviewFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            <div className="mt-4">
              <InterviewTable
                data={filtered}
                loading={loading}
                onViewFeedback={handleViewFeedback}
                onReschedule={handleReschedule}
                onCancel={handleCancel}
                onComplete={handleComplete}
                onStatusChange={handleStatusChange}
              />
            </div>
          </>
        )}

        {activeTab === "templates" && <RoundTemplateBuilder />}
      </div>

      <FeedbackForm
        show={feedbackModal.show}
        onClose={() =>
          setFeedbackModal({ show: false, interviewId: 0, candidateName: "" })
        }
        interviewId={feedbackModal.interviewId}
        candidateName={feedbackModal.candidateName}
        onSubmitted={handleFeedbackSubmitted}
      />

      <EditInterviewModal
        show={editModal.show}
        onClose={() => setEditModal({ show: false, interview: null })}
        interview={editModal.interview}
      />

      <Footer />
    </div>
  );
};

export default InterviewManagement;
